import { toParameterClass } from 'app/model/adapters/linkedservices/parameter-config.util';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';

import { CopySourceAdapter, commonCopySourceAdapts } from './copysource.adapter';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { RestResourceCopySource } from 'app/model/pipeline/copysource/rest-source.copysource';
import { isLogicAppExpression } from 'app/shared/utils/expression.util';
import moment from 'moment-timezone';
import { toParameterObject } from '../../linkedservices/parameter-config.util';
import { stringsAreEqualCaseInsensitive } from 'app/shared/utils/string.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(RestResourceCopySource),
    asynchronous: true,
    adapts: {
        ...commonCopySourceAdapts,
        httpRequestTimeout: '*',
        requestInterval: '*',
        requestMethod: '*',
        requestBody: '*',
        additionalHeaders: '*',
        paginationRules: '*'
    }
})
export class RestResourceCopySourceAdapter extends CopySourceAdapter<RestResourceCopySource, Model.RestSource> {
    protected override async _toResource(copySource: RestResourceCopySource): Promise<Model.RestSource> {
        const result: Model.RestSource = this.getCommonCopySourceProperties(copySource, 'RestSource');
        result.httpRequestTimeout = ValueAdapter.toResource(copySource, RestResourceCopySource.REQUEST_TIMEOUT);
        try {
            result.requestInterval =
                typeof copySource.requestInterval === 'number'
                    ? moment.duration(copySource.requestInterval, 'ms').format('dd.hh:mm:ss.SSS', {
                          trim: false
                      })
                    : ValueAdapter.toResource(copySource, RestResourceCopySource.REQUEST_INTERVAL);

            if (copySource.isV2) {
                const requestMethod = ValueAdapter.toResource(copySource, RestResourceCopySource.METHOD);
                result.requestMethod = requestMethod;
                result.requestBody =
                    typeof requestMethod === 'string' && stringsAreEqualCaseInsensitive(requestMethod, 'GET')
                        ? undefined
                        : ValueAdapter.toResource(copySource, RestResourceCopySource.BODY);
                result.additionalHeaders = toParameterObject(copySource.additionalHeaders);
                result.paginationRules = toParameterObject(copySource.paginationRules);
            }
        } catch {
            this._logError('Failed to convert requestInterval into TimeSpan');
        }
        return result;
    }

    protected override async _toModel(
        restSource: Model.RestSource,
        copyActivity: CopyActivity
    ): Promise<RestResourceCopySource> {
        const result = this.createCopySourceWithCommonProperties(
            RestResourceCopySource.MODEL,
            restSource,
            copyActivity
        ) as RestResourceCopySource;
        ValueAdapter.toModel(result, RestResourceCopySource.REQUEST_TIMEOUT, restSource.httpRequestTimeout);
        try {
            if (typeof restSource.requestInterval === 'string' && !isLogicAppExpression(restSource.requestInterval)) {
                result.requestInterval = moment.duration(restSource.requestInterval).asMilliseconds();
            } else {
                ValueAdapter.toModel(result, RestResourceCopySource.REQUEST_INTERVAL, restSource.requestInterval);
            }

            if (restSource.requestMethod != null) {
                result.isV2 = true;
                ValueAdapter.toModel(result, RestResourceCopySource.METHOD, restSource.requestMethod);
                result.additionalHeaders = toParameterClass(restSource.additionalHeaders, result);
                ValueAdapter.toModel(result, RestResourceCopySource.BODY, restSource.requestBody);
                if (
                    typeof restSource.requestMethod === 'string' &&
                    stringsAreEqualCaseInsensitive(restSource.requestMethod, 'GET') &&
                    restSource.requestBody
                ) {
                    result.containsRequestBodyInGetRequests = true;
                }
                result.paginationRules = toParameterClass(restSource.paginationRules, result);
            }
        } catch {
            this._logError('Failed to convert requestInterval into number');
        }
        return result;
    }
}
